import { TableHead, Th, Tr, Td } from '../../Elements/DataTable';
import SectionHeader from '../../Elements/SectionHeader';
import Button from '../../Elements/Button';
import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { UserContext } from '../../../context/UserContext';
import ConfirmDialog from '../../Elements/ConfirmDialog';
import CustomerModal from '../CustomerComponents/CustomerModal';
import QRModal from '../CustomerComponents/QRModal';
import {
  CheckCircle2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  MoreVertical, QrCode, XCircle,
} from 'lucide-react';
import useAuthImage, { withImageVariant } from '../../../lib/hooks/useAuthImage';
import './PreInscripcionesPage.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

// `useAuthImage` vivía aquí y solo lo usaba esta pantalla, mientras la ficha del
// socio repetía el mismo bug con su propia copia. Ahora es de todos:
// src/lib/hooks/useAuthImage.js

// Lista/ficha: miniatura (`?variant=thumb`). El visor modal pide el path SIN variant
// (full). Misma caché, claves distintas → no se pisan (Sentry large payload lista).

// ── Small image thumbnail ──────────────────────────────────────────────────────

// Exportada para el candado de carga diferida: el test necesita el  atado a
// un nodo real, porque quitarlo rompe el diferido en silencio.
export function ImageThumb({ path, token, alt, onOpen }) {
  const { src, loading, ref } = useAuthImage(withImageVariant(path, 'thumb'), token);

  if (!path) return <span className="text-gray-300 text-xs">—</span>;
  // El hueco lleva el `ref`: es lo que le dice al observador que esta fila ya se
  // ve. Sin él la imagen no se pediría nunca.
  if (loading || !src) {
    return (
      <div
        ref={ref}
        className={`w-10 h-10 rounded-lg flex-shrink-0 ${loading ? 'bg-gray-100 animate-pulse' : 'bg-gray-50'}`}
        aria-label={loading ? `Cargando ${alt}` : alt}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen({ path, alt });
      }}
      className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-lg"
      title={`Ver ${alt}`}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="pointer-events-none w-10 h-10 rounded-lg object-cover border border-gray-200 hover:opacity-80 hover:border-green-400 transition-all cursor-zoom-in"
      />
    </button>
  );
}

// ── Photo avatar (round, with click to open) ──────────────────────────────────

function AvatarThumb({ path, token, name, onOpen }) {
  const { src, loading, ref } = useAuthImage(withImageVariant(path, 'thumb'), token);

  if (loading || (path && !src)) {
    return (
      <div
        ref={ref}
        className={`w-9 h-9 rounded-full flex-shrink-0 ${loading ? 'bg-gray-100 animate-pulse' : 'bg-gray-50'}`}
        aria-label={loading ? `Cargando foto de ${name}` : name}
      />
    );
  }

  if (src) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen({ path, alt: name });
        }}
        className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-full"
        title="Ver foto"
      >
        <img
          src={src}
          alt={name}
          draggable={false}
          className="pointer-events-none w-9 h-9 rounded-full object-cover border border-gray-200 hover:opacity-80 hover:border-green-400 transition-all cursor-zoom-in"
        />
      </button>
    );
  }

  return (
    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0 text-sm">
      👤
    </div>
  );
}

// ── Modal vista documento / foto ───────────────────────────────────────────────

function DocumentPreviewModal({ preview, token, onClose }) {
  const { path, alt } = preview || {};
  // `diferida: false` NO es un detalle: este visor no ata el `ref` a ningún nodo,
  // así que con el diferido puesto `aLaVista` no se encendía nunca y una imagen sin
  // cachear no se pedía jamás. Y como la salida temprana deja `src` intacto, el modal
  // enseñaba el documento ANTERIOR bajo el título nuevo — el anverso rotulado
  // «DNI reverso». Ver es lo único que hace esta pantalla: aquí no se difiere nada.
  const { src, loading } = useAuthImage(path, token, { diferida: false });

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [preview, onClose]);

  if (!preview || typeof document === 'undefined' || !document.body) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt || 'Vista de documento'}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900 truncate">{alt || 'Documento'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cerrar
          </button>
        </div>
        <div className="flex items-center justify-center bg-gray-50 p-4 min-h-[240px] max-h-[calc(90vh-64px)] overflow-auto">
          {loading ? (
            <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
          ) : src ? (
            <img
              src={src}
              alt={alt || 'Documento'}
              draggable={false}
              className="max-w-full max-h-[calc(90vh-120px)] object-contain rounded-lg"
            />
          ) : (
            <p className="text-sm text-gray-500">No se pudo cargar la imagen.</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Estado ────────────────────────────────────────────────────────────────────

const STATUS_LABEL = { pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada' };

function StatusBadge({ status }) {
  return (
    <span className={`pre-status pre-status--${status || 'pending'}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// ── Ficha de la solicitud ─────────────────────────────────────────────────────

/** Edad a día de hoy, para poder decidir un alta sin hacer cuentas mentales. */
function edadDe(iso) {
  if (!iso) return null;
  const nac = new Date(iso);
  if (Number.isNaN(nac.getTime())) return null;
  const hoy = new Date();
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad -= 1;
  return edad;
}

function Dato({ etiqueta, children }) {
  return (
    <div className="pre-dato">
      <span className="pre-dato-label">{etiqueta}</span>
      <span className="pre-dato-valor">{children || '—'}</span>
    </div>
  );
}

/** Documento grande dentro de la ficha; al pulsarlo se abre el visor de siempre. */
function DocGrande({ path, token, alt, onOpen }) {
  const { src, loading, ref } = useAuthImage(withImageVariant(path, 'thumb'), token);
  if (!path) {
    return (
      <div className="pre-doc-grande pre-doc-grande--vacio">
        <span>{alt}</span>
        <span className="pre-doc-grande-falta">No aportado</span>
      </div>
    );
  }
  return (
    <button ref={ref} type="button" className="pre-doc-grande" onClick={() => onOpen({ path, alt })} title={`Ver ${alt}`}>
      {loading ? <span className="pre-doc-grande-falta">Cargando…</span> : null}
      {src ? <img src={src} alt={alt} draggable={false} /> : null}
      <span className="pre-doc-grande-pie">{alt}</span>
    </button>
  );
}

/**
 * Ficha de una solicitud: TODO lo que mandó el solicitante, con los documentos en grande y
 * las dos acciones dentro.
 *
 * Existe por el reporte de CRUO del 24-08: tocar la fila no abría nada —no había clic de
 * fila, solo las miniaturas y el ⋮— y el ⋮ es un botón diminuto en la última columna de una
 * tabla de 900 px que en un iPad obliga a arrastrar de lado para llegar.
 *
 * Y de paso enseña la FECHA DE NACIMIENTO, que la tabla no pinta en ninguna columna: hasta
 * hoy se aprobaban solicitudes sin ver la edad del solicitante.
 *
 * Ojo: una preinscripción NO es un socio y no tiene ficha de socio — esa nace al aprobarla.
 */
function SolicitudModal({ item, token, onClose, onApprove, onReject, onOpenDoc }) {
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item || typeof document === 'undefined' || !document.body) return null;

  const nombre = `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Solicitud';
  const edad = edadDe(item.birth_date);
  const nacimiento = item.birth_date
    ? `${new Date(item.birth_date).toLocaleDateString('es-ES')}${edad != null ? ` · ${edad} años` : ''}`
    : null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Solicitud de ${nombre}`}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">{nombre}</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Solicitud del {new Date(item.created_at).toLocaleDateString('es-ES')}
            </p>
          </div>
          <StatusBadge status={item.status} />
        </div>

        <div className="overflow-auto px-5 py-4">
          <div className="pre-ficha-datos">
            <Dato etiqueta="DNI">{item.dni}</Dato>
            <Dato etiqueta="Fecha de nacimiento">{nacimiento}</Dato>
            <Dato etiqueta="Teléfono">{item.phone}</Dato>
            <Dato etiqueta="Email">{item.email}</Dato>
            <Dato etiqueta="Dirección">{item.address}</Dato>
            {item.notes ? <Dato etiqueta="Notas">{item.notes}</Dato> : null}
          </div>

          <div className="pre-ficha-docs">
            <DocGrande path={item.photo_path} token={token} alt="Foto" onOpen={onOpenDoc} />
            <DocGrande path={item.dni_front_path} token={token} alt="DNI anverso" onOpen={onOpenDoc} />
            <DocGrande path={item.dni_back_path} token={token} alt="DNI reverso" onOpen={onOpenDoc} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" className="pre-btn" onClick={onClose}>Cerrar</button>
          {/* Aprobar y rechazar solo mientras está pendiente: en cualquier otro estado el
              backend responde 400 "already processed". Mismo criterio que el menú ⋮. */}
          {item.status === 'pending' && (
            <>
              <button type="button" className="pre-btn pre-btn--danger" onClick={() => onReject(item)}>
                <XCircle size={16} aria-hidden="true" /> Rechazar
              </button>
              <button type="button" className="pre-btn pre-btn--primary" onClick={() => onApprove(item)}>
                <CheckCircle2 size={16} aria-hidden="true" /> Aprobar
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Menú de acciones de la fila ───────────────────────────────────────────────

function RowActions({ item, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);   // { top, left } en coordenadas de ventana
  const ref = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // El menú va FIJO a la ventana: si la página se desplaza o cambia de tamaño se queda
    // colgado donde estaba, así que se cierra en vez de mentir sobre a qué fila pertenece.
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  /* Colocar el menú: debajo del botón si cabe, y arriba si no.
   *
   * Antes era `position: absolute` dentro de `.pre-scroll`, que lleva `overflow-x: auto`.
   * Por la regla de CSS, cuando un eje deja de ser `visible` el otro pasa de `visible` a
   * `auto`: ese contenedor recorta TAMBIÉN por arriba y por abajo aunque nadie escribiera
   * `overflow-y`. Y por fuera, `.pre-surface` lleva `overflow: hidden`. Con una sola fila el
   * contenedor mide una fila de alto y el menú se veía cortado casi entero — que es
   * exactamente lo que reportó CRUO el 24-08 («al menos cuando solo hay un socio»). */
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const b = btnRef.current.getBoundingClientRect();
    const alto = menuRef.current?.offsetHeight || 96;
    const margen = 8;
    const cabeDebajo = b.bottom + 4 + alto + margen <= window.innerHeight;
    const top = cabeDebajo
      ? b.bottom + 4
      : Math.max(margen, b.top - 4 - alto);
    // Alineado por la derecha con el botón, sin salirse por ningún lado.
    const ancho = menuRef.current?.offsetWidth || 168;
    const left = Math.min(
      Math.max(margen, b.right - ancho),
      Math.max(margen, window.innerWidth - ancho - margen)
    );
    setPos({ top, left });
  }, [open]);

  // Aprobar y rechazar solo son válidos mientras está pendiente: el backend
  // responde 400 "already processed" en cualquier otro estado. Una vez resuelta
  // no queda ninguna acción, así que la fila no muestra menú.
  if (item.status !== 'pending') return <span className="pre-date">—</span>;

  const menu = (
    <div
      ref={menuRef}
      className="pre-menu"
      role="menu"
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        role="menuitem"
        className="pre-menu-item"
        onClick={() => { setOpen(false); onApprove(item); }}
      >
        <CheckCircle2 size={16} /> Aprobar
      </button>
      <span className="pre-menu-sep" />
      <button
        type="button"
        role="menuitem"
        className="pre-menu-item pre-menu-item--danger"
        onClick={() => { setOpen(false); onReject(item); }}
      >
        <XCircle size={16} /> Rechazar
      </button>
    </div>
  );

  return (
    <div className="pre-actions" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        className="pre-icon-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Acciones de ${item.first_name} ${item.last_name}`}
        onClick={() => setOpen((v) => !v)}
      >
        <MoreVertical size={16} />
      </button>
      {/* Al `document.body`: dentro de la tabla lo recortaban sus contenedores. */}
      {open && typeof document !== 'undefined' && document.body
        ? ReactDOM.createPortal(menu, document.body)
        : null}
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

/** Filas por página. El listado no pagina en servidor, así que se trocea aquí. */
const PAGE_SIZE = 11;

/**
 * Sin pestaña «Rechazadas»: rechazar borra la solicitud de verdad
 * (`pre_registration.py` → `db.delete`), no la marca como rechazada, así que
 * el filtro `status=rejected` no puede devolver nada nunca. Es una decisión de
 * retención de datos —no conservar DNI ni fotos de gente rechazada— tomada el
 * 8 ago 2026, no un olvido.
 */
const TABS = [
  { value: '',         label: 'Todas' },
  { value: 'approved', label: 'Aprobadas' },
  { value: 'pending',  label: 'Pendientes' },
];

export default function PreInscripcionesPage() {
  const [token, , , venueId] = useContext(UserContext);

  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('');
  const [error,   setError]   = useState('');
  const [page,    setPage]    = useState(1);
  const [toast,   setToast]   = useState(null);
  const [sociosCap, setSociosCap] = useState(null);

  // Ficha de la solicitud: se abre tocando la fila (antes la fila no hacía nada).
  const [solicitud, setSolicitud] = useState(null);

  // Vista ampliada de foto / DNI
  const [docPreview, setDocPreview] = useState(null);
  const openDocPreview = useCallback((preview) => setDocPreview(preview), []);
  const closeDocPreview = useCallback(() => setDocPreview(null), []);

  // Solicitud a la espera de que se confirme el rechazo (borra de verdad).
  const [pendienteRechazo, setPendienteRechazo] = useState(null);

  // QR de preinscripción
  const [qrModalOpen, setQrModalOpen] = useState(false);

  // Create socio modal
  const [createModalOpen,    setCreateModalOpen]    = useState(false);
  const [prefillClientData,  setPrefillClientData]  = useState(null);
  const [creatingFromPreReg, setCreatingFromPreReg] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const showToast = useCallback((text, kind = 'ok') => {
    setToast({ text, kind });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const refreshPendingBadge = async () => {
    if (!venueId) return;
    try {
      const response = await axios.get(`/v1/venues/${venueId}/pre-registrations/count`, {
        headers: { ...headers, 'Cache-Control': 'no-cache, no-store, max-age=0', Pragma: 'no-cache' },
        params: { _ts: Date.now() },
      });
      window.dispatchEvent(new CustomEvent('prereg-count-updated', {
        detail: { pending: response.data.pending || 0 },
      }));
    } catch {
      window.dispatchEvent(new CustomEvent('prereg-count-updated'));
    }
  };

  const fetchItems = async () => {
    if (!venueId) return;
    setLoading(true); setError('');
    try {
      const params = filter ? { status: filter } : {};
      const r = await axios.get(`/v1/venues/${venueId}/pre-registrations`, { headers, params });
      setItems(Array.isArray(r.data) ? r.data : []);
      refreshPendingBadge();
    } catch {
      setError('Error al cargar las preinscripciones.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [venueId, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Contador "47/120 socios" — mismo origen que en Socios.
  useEffect(() => {
    if (!token || !venueId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/v1/venues/${venueId}`, { headers });
        if (!cancelled && (res.data.max_socios != null || res.data.socios_count != null)) {
          setSociosCap({
            socios_count: res.data.socios_count ?? 0,
            max_socios: res.data.max_socios ?? null,
          });
        }
      } catch {
        if (!cancelled) setSociosCap(null);
      }
    })();
    return () => { cancelled = true; };
  }, [token, venueId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al cambiar de pestaña se vuelve a la primera página.
  useEffect(() => { setPage(1); }, [filter]);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleItems = useMemo(
    () => items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [items, currentPage]
  );

  const openCreateFromPreRegistration = (item) => {
    setSolicitud(null);
    setCreatingFromPreReg(item);
    setPrefillClientData({
      preRegistrationId: item.id,
      nombre:    item.first_name,
      apellidos: item.last_name,
      dni:       item.dni || '',
      telefono:  item.phone || '',
      email:     item.email || '',
      direccion: item.address || '',
      ciudad:    item.city || '',
      cp:        item.postal_code ? String(item.postal_code) : '',
      fecha_nacimiento: item.birth_date
        ? (() => { const [fy, fm, fd] = item.birth_date.split('-'); return `${fd}/${fm}/${fy}`; })()
        : '',
      photo_path:     item.photo_path     || null,
      dni_front_path: item.dni_front_path || null,
      dni_back_path:  item.dni_back_path  || null,
      // Firmó en su móvil al preinscribirse: el alta la recoge y no se le vuelve
      // a pedir. (ACTA_FIRMA_PLAN.md F3)
      signatureUrl:   item.signature_path  || null,
    });
    setCreateModalOpen(true);
  };

  // El backend BORRA la preinscripción al rechazar (no la marca), así que se avisa de que no
  // hay vuelta atrás. Con `window.confirm` ese aviso se lo tragaba la tablet del club y
  // rechazar no hacía NADA — el mismo fallo que «Cancelar» en Barra (reporte 24-08).
  const handleReject = (item) => setPendienteRechazo(item);

  const rechazar = async (item) => {
    try {
      await axios.put(`/v1/venues/${venueId}/pre-registrations/${item.id}/reject`, {}, { headers });
      setSolicitud(null);
      await fetchItems();
      showToast('Solicitud de inscripción rechazada');
    } catch {
      setError('Error al rechazar la preinscripción.');
    }
  };

  const capLabel = sociosCap && sociosCap.max_socios != null
    ? `${sociosCap.socios_count}/${sociosCap.max_socios} socios`
    : sociosCap
    ? `${sociosCap.socios_count} socios`
    : null;
  const capFull = sociosCap?.max_socios != null && sociosCap.socios_count >= sociosCap.max_socios;

  return (
    <>
      <div className="pre-page">
        {/* Era la tercera implementación a mano de lo mismo: título de 20/500 en
            vez de 24/600, su propia píldora de recuento en gris de 12, y un
            botón de radio 8 con su propio padding. Ahora, los del sistema. */}
        <SectionHeader
          title="Preinscripciones"
          count={capLabel || null}
          countTone={capFull ? 'alert' : 'default'}
          sticky={false}
          actionsClassName="items-center"
        >
          <Button onClick={() => setQrModalOpen(true)}>
            <QrCode size={16} aria-hidden="true" />
            QR Preinscripción
          </Button>
        </SectionHeader>

        <div className="pre-tabs" role="tablist" aria-label="Filtrar preinscripciones">
          {TABS.map((tab) => (
            <button
              key={tab.value || 'all'}
              type="button"
              role="tab"
              aria-selected={filter === tab.value}
              className={`pre-tab${filter === tab.value ? ' is-active' : ''}`}
              onClick={() => setFilter(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && <div className="pre-error" role="alert">{error}</div>}

        <div className="pre-surface">
          {loading ? (
            <div className="pre-state">Cargando preinscripciones…</div>
          ) : items.length === 0 ? (
            <div className="pre-state">
              No hay preinscripciones{filter ? ` ${TABS.find((t) => t.value === filter).label.toLowerCase()}` : ''}.
            </div>
          ) : (
            <>
              <div className="pre-scroll">
                <table className="pre-table">
                  <TableHead>
              <Th>Solicitante</Th>
              <Th>DNI</Th>
              <Th>Contacto</Th>
              <Th>Documentos</Th>
              <Th>Fecha</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </TableHead>
                  <tbody>
                    {visibleItems.map((item) => (
                      // Tocar la fila abre la ficha: antes no había clic de fila y el único
                      // camino era el ⋮ del final de una tabla de 900 px (reporte iPad 24-08).
                      // Las miniaturas y el ⋮ paran la propagación y siguen haciendo lo suyo.
                      <Tr
                        key={item.id}
                        className="pre-fila-pulsable"
                        onClick={() => setSolicitud(item)}
                      >
                        <Td>
                          <div className="pre-applicant">
                            <AvatarThumb
                              path={item.photo_path}
                              token={token}
                              name={`${item.first_name} ${item.last_name}`}
                              onOpen={openDocPreview}
                            />
                            <div>
                              <p className="pre-applicant-name">{item.first_name} {item.last_name}</p>
                              {item.address && <p className="pre-applicant-sub">{item.address}</p>}
                            </div>
                          </div>
                        </Td>

                        <Td className="pre-dni">{item.dni || '—'}</Td>

                        <Td>
                          <div className="pre-contact">
                            {item.phone && <p className="pre-contact-phone">{item.phone}</p>}
                            {item.email && <p className="pre-contact-mail">{item.email}</p>}
                            {!item.phone && !item.email && <p className="pre-contact-mail">—</p>}
                          </div>
                        </Td>

                        <Td>
                          <div className="pre-docs">
                            <div className="pre-doc">
                              <ImageThumb
                                path={item.dni_front_path}
                                token={token}
                                alt="DNI anverso"
                                onOpen={openDocPreview}
                              />
                              {item.dni_front_path && <span className="pre-doc-label">Anverso</span>}
                            </div>
                            <div className="pre-doc">
                              <ImageThumb
                                path={item.dni_back_path}
                                token={token}
                                alt="DNI reverso"
                                onOpen={openDocPreview}
                              />
                              {item.dni_back_path && <span className="pre-doc-label">Reverso</span>}
                            </div>
                          </div>
                        </Td>

                        <Td className="pre-date">
                          {new Date(item.created_at).toLocaleDateString('es-ES')}
                        </Td>

                        <Td><StatusBadge status={item.status} /></Td>

                        <Td>
                          <RowActions
                            item={item}
                            onApprove={openCreateFromPreRegistration}
                            onReject={handleReject}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pre-pagination">
                <span className="pre-pagination-text">Página {currentPage} de {totalPages}</span>
                <div className="pre-pagination-nav">
                  <button
                    type="button" className="pre-page-btn" aria-label="Primera página"
                    disabled={currentPage === 1} onClick={() => setPage(1)}
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    type="button" className="pre-page-btn" aria-label="Página anterior"
                    disabled={currentPage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button" className="pre-page-btn" aria-label="Página siguiente"
                    disabled={currentPage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    type="button" className="pre-page-btn" aria-label="Última página"
                    disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {toast && (
        <div className={`pre-toast${toast.kind === 'error' ? ' pre-toast--error' : ''}`} role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          {toast.text}
        </div>
      )}

      <SolicitudModal
        item={solicitud}
        token={token}
        onClose={() => setSolicitud(null)}
        onApprove={openCreateFromPreRegistration}
        onReject={handleReject}
        onOpenDoc={openDocPreview}
      />

      <ConfirmDialog
        active={!!pendienteRechazo}
        title="¿Rechazar esta solicitud?"
        description={
          pendienteRechazo
            ? `Se eliminará la solicitud de ${pendienteRechazo.first_name} ${pendienteRechazo.last_name} ` +
              'junto con su foto y su DNI, y no se podrá recuperar.'
            : null
        }
        tone="danger"
        confirmLabel="Rechazar y eliminar"
        onConfirm={() => {
          const item = pendienteRechazo;
          setPendienteRechazo(null);
          if (item) rechazar(item);
        }}
        onCancel={() => setPendienteRechazo(null)}
      />

      <DocumentPreviewModal preview={docPreview} token={token} onClose={closeDocPreview} />

      {qrModalOpen && (
        <QRModal venueId={venueId} token={token} onClose={() => setQrModalOpen(false)} />
      )}

      {createModalOpen && (
        <CustomerModal
          active={createModalOpen}
          handleModal={() => {
            setCreateModalOpen(false);
            setPrefillClientData(null);
            setCreatingFromPreReg(null);
          }}
          token={token}
          id={null}
          prefillData={prefillClientData}
          shopId={venueId}
          onClientCreated={async (_client, meta) => {
            try {
              if (creatingFromPreReg) {
                await axios.put(
                  `/v1/venues/${venueId}/pre-registrations/${creatingFromPreReg.id}/approve`,
                  { expiry_date: meta?.expiration_date, notes: null },
                  { headers },
                );
              }
              setCreateModalOpen(false);
              setPrefillClientData(null);
              setCreatingFromPreReg(null);
              await fetchItems();
              showToast('Solicitud de inscripción aprobada');
            } catch {
              setError('El socio se creó, pero no se pudo marcar la preinscripción como aprobada.');
            }
          }}
        />
      )}
    </>
  );
}
